import { AssistantResourceBlock } from '@mews-commander/data-access';
import { RouteName } from '@mews-commander/enums';
import { IconName } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { ResultListProps } from '../results/ListTitle';
import { highlight } from '../../utils/highlight';
import { navigateInBaseModal } from '../../../app/actions/common';
import { stringifyDateRange } from '../../utils/array';
import { StyledLeftIcon } from '../styles';
import { SearchType } from '../../assistantSearchTypes';
import { useLocalizedDate } from './useLocalizedDate';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface ResourceBlockListProps extends ResultListProps {
    resourceBlocks: AssistantResourceBlock[];
}

const useFormattedResourceBlockDates = ({ startUtc, endUtc }: AssistantResourceBlock) => {
    const localizedStartDate = useLocalizedDate(startUtc);
    const localizedEndDate = useLocalizedDate(endUtc);
    return stringifyDateRange([localizedStartDate, localizedEndDate]);
};

const useResourceBlockDescription = (resourceBlock: AssistantResourceBlock): string =>
    useFormattedResourceBlockDates(resourceBlock);

interface PrimaryTextProps {
    resourceBlock: AssistantResourceBlock;
    searchTerm: string;
}

const PrimaryText = ({ resourceBlock, searchTerm }: PrimaryTextProps) => {
    const text = highlight(resourceBlock.name ?? '', searchTerm);
    return <>{text}</>;
};

interface SecondaryTextProps {
    resourceBlock: AssistantResourceBlock;
    searchTerm: string;
}

const SecondaryText = ({ resourceBlock, searchTerm }: SecondaryTextProps) => {
    const description = useResourceBlockDescription(resourceBlock);
    return <span>{highlight(description, searchTerm)}</span>;
};

export const useResourceBlockList = ({
    resourceBlocks,
    searchTerm,
}: ResourceBlockListProps): Array<AssistantSearchListItemProps> => {
    const resolveRoute = useResolveRoute();

    return resourceBlocks.map((resourceBlock) => {
        const url = resolveRoute(RouteName.ResourceBlockDetail, { id: resourceBlock.id });
        return {
            itemId: resourceBlock.id,
            primaryText: <PrimaryText resourceBlock={resourceBlock} searchTerm={searchTerm} />,
            secondaryText: <SecondaryText resourceBlock={resourceBlock} searchTerm={searchTerm} />,
            leftIcon: <StyledLeftIcon size="small" value={IconName.Reservations} />,
            routeAction: navigateInBaseModal(url),
            searchType: SearchType.ResourceBlock,
        };
    });
};
